import { NavLink, useLocation } from 'react-router-dom'
import { LayoutDashboard, Cpu, Users, Download, Server, Package, FileText, Menu, LogOut, X, Ticket, Calendar, TrendingUp, Target, Wrench, Activity, DollarSign, Bell, Shield, ChevronDown, ChevronUp, UserCircle, Mail, Handshake, ShoppingBag, Wallet, Link2, Megaphone, Share2 } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { suggestionService, resellerService } from '../services/AdminServices'
import './Layout.css'

const NAV_SECTIONS = [
  {
    key: 'core',
    label: 'Operativo',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Overview', end: true, perm: 'overview' },
      { to: '/mi-dashboard', icon: UserCircle, label: 'Mi Dashboard', perm: 'overview', fav: true, favOrder: 0 },
      { to: '/devices', icon: Cpu, label: 'Dispositivos', perm: 'devices' },
      { to: '/clients', icon: Users, label: 'Clientes', perm: 'clients' },
      { to: '/tickets', icon: Ticket, label: 'Soporte', perm: 'tickets' },
      { to: '/calendar', icon: Calendar, label: 'Calendario', perm: 'calendar', fav: true, favOrder: 1 },
      { to: '/inventory', icon: Package, label: 'Inventario', perm: 'inventory' },
      { to: '/firmware', icon: Download, label: 'Firmware', perm: 'firmware' },
    ]
  },
  {
    key: 'comercial',
    label: 'Comercial & Finanzas',
    items: [
      { to: '/invoices', icon: FileText, label: 'Facturación', perm: 'invoices' },
      { to: '/finance', icon: DollarSign, label: 'Finanzas', perm: 'finance' },
      { to: '/pipeline', icon: TrendingUp, label: 'Pipeline', perm: 'pipeline', fav: true, favOrder: 2 },
      { to: '/resellers', icon: Handshake, label: 'Revendedores', perm: 'pipeline' },
      { to: '/email-marketing', icon: Mail, label: 'Email Marketing', perm: 'pipeline' },
      { to: '/social-media', icon: Share2, label: 'Redes Sociales', perm: 'pipeline' },
      { to: '/goals', icon: Target, label: 'Metas', perm: 'goals', fav: true, favOrder: 3 },
    ]
  },
  {
    key: 'admin',
    label: 'Administración',
    items: [
      { to: '/notifications', icon: Bell, label: 'Notificaciones', perm: 'notifications' },
      { to: '/tools', icon: Wrench, label: 'Herramientas', perm: 'tools' },
      { to: '/audit', icon: Activity, label: 'Auditoría', perm: 'audit' },
      { to: '/admin-users', icon: Shield, label: 'Admin Users', perm: 'users' },
      { to: '/system', icon: Server, label: 'Sistema', perm: 'system' },
    ]
  },
]

const RESELLER_NAV_SECTIONS = [
  {
    key: 'reseller',
    label: 'Mi Portal',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true, perm: 'reseller-portal' },
      { to: '/my-sales', icon: ShoppingBag, label: 'Mis Ventas', perm: 'reseller-portal' },
      { to: '/my-payouts', icon: Wallet, label: 'Mis Pagos', perm: 'reseller-portal' },
      { to: '/my-link', icon: Ticket, label: 'Mi Cupón', perm: 'reseller-portal' },
      { to: '/my-profile', icon: UserCircle, label: 'Mi Perfil', perm: 'reseller-portal' },
      { to: '/my-tools', icon: Megaphone, label: 'Herramientas', perm: 'reseller-portal' },
    ]
  },
]

function Layout({ children }) {
  const { user, handleLogout, hasPermission, adminRole, isReseller, isAlsoReseller } = useAuth()
  const location = useLocation()
  const sections = isReseller
    ? RESELLER_NAV_SECTIONS
    : isAlsoReseller
      ? [...NAV_SECTIONS, ...RESELLER_NAV_SECTIONS]
      : NAV_SECTIONS

  // Find which section contains the current route
  const findActiveSection = (pathname) => {
    for (const s of sections) {
      if (s.items.some(item => item.end ? pathname === item.to : pathname.startsWith(item.to) && item.to !== '/')) {
        return s.key
      }
    }
    // Default: first section for '/' route
    return sections[0]?.key
  }

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [openSections, setOpenSections] = useState(() => {
    const activeKey = findActiveSection(window.location.pathname)
    return Object.fromEntries(sections.map(s => [s.key, s.key === activeKey]))
  })

  // Auto-expand section when route changes
  useEffect(() => {
    const activeKey = findActiveSection(location.pathname)
    if (activeKey) {
      setOpenSections(Object.fromEntries(sections.map(s => [s.key, s.key === activeKey])))
    }
  }, [location.pathname])

  // When sections change (e.g. reseller section added after API load), ensure new sections have a default
  useEffect(() => {
    setOpenSections(prev => {
      const updated = { ...prev }
      for (const s of sections) {
        if (!(s.key in updated)) updated[s.key] = false
      }
      return updated
    })
  }, [sections.length])
  const [pendingSuggestions, setPendingSuggestions] = useState(0)
  const [pendingResellers, setPendingResellers] = useState(0)
  const [footerOpen, setFooterOpen] = useState(false)
  const footerRef = useRef(null)

  // Accordion: clicking a section closes all others
  const toggleSection = (key) => {
    setOpenSections(prev => {
      const isCurrentlyOpen = prev[key]
      // Close all, then toggle the clicked one
      const allClosed = Object.fromEntries(Object.keys(prev).map(k => [k, false]))
      return { ...allClosed, [key]: !isCurrentlyOpen }
    })
  }

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (!mobile) setSidebarOpen(true)
      else setSidebarOpen(false)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    suggestionService.list({ status: 'pending' })
      .then(res => setPendingSuggestions((res.suggestions || []).length))
      .catch(() => {})
    resellerService.list()
      .then(res => setPendingResellers((res.resellers || []).filter(r => r.status === 'pending').length))
      .catch(() => {})
  }, [])

  const closeSidebar = () => {
    if (isMobile) setSidebarOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (footerRef.current && !footerRef.current.contains(e.target)) {
        setFooterOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="layout">
      {/* Mobile header */}
      {isMobile && (
        <header className="mobile-header">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu size={22} />
          </button>
          <span className="mobile-header-title">GrowPro Admin</span>
          <div className="mobile-header-status">
            <span className="status-dot"></span>
          </div>
        </header>
      )}

      {/* Overlay */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'} ${isMobile ? 'mobile' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-text">{isReseller ? 'GrowPro Reseller' : isAlsoReseller ? 'GrowPro Admin + Reseller' : 'GrowPro Admin'}</span>
          </div>
          <button 
            className="menu-toggle" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {isMobile ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="nav-menu">
          {sections.map(section => {
            const visibleItems = section.items.filter(item => hasPermission(item.perm))
            if (visibleItems.length === 0) return null
            return (
            <div key={section.key} className="nav-section">
              <button
                className={`nav-section-header ${openSections[section.key] ? 'open' : ''}`}
                onClick={() => toggleSection(section.key)}
              >
                <span>{section.label}</span>
                <ChevronDown size={14} className="nav-section-chevron" />
              </button>
              {openSections[section.key] && (
                <div className="nav-section-items">
                  {visibleItems.map(item => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className="nav-item"
                      end={item.end}
                      onClick={closeSidebar}
                    >
                      <item.icon size={18} />
                      <span>{item.label}</span>
                      {item.to === '/goals' && pendingSuggestions > 0 && (
                        <span className="nav-badge">{pendingSuggestions}</span>
                      )}
                      {item.to === '/resellers' && pendingResellers > 0 && (
                        <span className="nav-badge">{pendingResellers}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
            )
          })}
        </nav>

        <div className="sidebar-footer sidebar-footer-compact" ref={footerRef}>
          {footerOpen && (
            <div className="footer-popup">
              {user && (
                <div className="user-details">
                  <span className="user-email">{user.email}</span>
                  <span className="user-role">{adminRole === 'superadmin' ? 'Super Admin' : adminRole === 'soporte' ? 'Soporte' : adminRole === 'ventas' ? 'Ventas' : adminRole === 'viewer' ? 'Viewer' : 'Admin'}</span>
                </div>
              )}
              <button 
                className="logout-button" 
                onClick={handleLogout}
                title="Cerrar sesión"
              >
                <LogOut size={16} />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          )}
          <button className="footer-toggle" onClick={() => setFooterOpen(!footerOpen)}>
            <UserCircle size={20} />
            <span className="footer-toggle-email">{user?.email?.split('@')[0] || 'Usuario'}</span>
            <ChevronUp size={14} className={`footer-chevron ${footerOpen ? 'open' : ''}`} />
          </button>
        </div>
      </aside>

      <main className={`main-content ${isMobile ? 'mobile' : ''}`}>
        {children}
      </main>
    </div>
  )
}

export default Layout
